// Single policy for editorial-guide canonical URLs and indexing decisions.
// Pure functions only: the same rules are used by DynamicPage at runtime and
// (via the manifest) by the sitemap and head-prerender scripts.
//
// Temporary source of indexing approval: seo/sitemap-manifest.json (entries with status "approved").
// The client only receives the derived list of approved paths (src/generated/seo-approved-paths.json,
// written by scripts/generate-sitemap.mjs), never the manifest's notes/evidence.
// A future editorial dashboard may replace it.

export const SITE_ORIGIN = 'https://www.techieday.com';
export const MIN_CONTENT_TEXT_CHARS = 200;
export const ROBOTS_INDEX = 'index, follow';
export const ROBOTS_NOINDEX = 'noindex, follow';

const SEGMENT_RE = /^[A-Za-z0-9\-._~]+$/;

/** Site-relative path -> normalized path (no query/hash, no trailing slash except "/"), or null if invalid. */
export function normalizePath(input) {
  if (typeof input !== 'string') return null;
  let path = input.split(/[?#]/)[0];
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  if (path === '/') return path;
  const segments = path.slice(1).split('/');
  const ok = segments.every((s) => SEGMENT_RE.test(s) && s !== '.' && s !== '..');
  return ok ? path : null;
}

/** Absolute canonical URL on the primary host, or null for an invalid path. */
export function canonicalUrl(input) {
  const path = normalizePath(input);
  return path === null ? null : `${SITE_ORIGIN}${path}`;
}

/** Approved paths from a full manifest (build-time scripts and tests). */
export function approvedPaths(manifest) {
  const entries = Array.isArray(manifest?.entries) ? manifest.entries : [];
  return new Set(entries.filter((e) => e?.status === 'approved').map((e) => normalizePath(e.path)).filter(Boolean));
}

function toPathSet(approved) {
  if (!approved || typeof approved[Symbol.iterator] !== 'function' || typeof approved === 'string') return new Set();
  return new Set([...approved].map(normalizePath).filter(Boolean));
}

function visibleTextLength(html) {
  if (typeof html !== 'string') return 0;
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;|\s+/g, ' ').trim().length;
}

/**
 * Decide indexing for an editorial guide route.
 * Indexable only when ALL hold: valid route, page exists, published, meaningful content,
 * and the route is explicitly approved (`approved` = list of approved paths).
 * The content-length check only guards against empty pages; it says nothing about editorial quality. Otherwise noindex and NO canonical
 * (a noindex page must never declare a canonical pointing elsewhere or at an empty page).
 */
export function evaluateGuide({ path, page, approved }) {
  const reasons = [];
  const normalized = normalizePath(path);
  if (normalized === null) reasons.push('invalid-path');
  if (!page) reasons.push('page-missing');
  else {
    if (page.status !== 'published') reasons.push('not-published');
    if (visibleTextLength(page.content) < MIN_CONTENT_TEXT_CHARS) reasons.push('no-meaningful-content');
  }
  if (normalized !== null && !toPathSet(approved).has(normalized)) reasons.push('not-approved');

  const index = reasons.length === 0;
  const robots = index ? ROBOTS_INDEX : ROBOTS_NOINDEX;
  return { index, robots, canonical: index ? canonicalUrl(normalized) : null, reasons };
}

const PRODUCT_RE = /^\/dp\/([A-Za-z0-9]{10})$/;

/**
 * Indexing decision for every route that is NOT an editorial guide or a not-found page
 * (those two decide for themselves and claim robots ownership).
 *
 * - Any query string (search, sort, filter, pagination, tracking) -> noindex, no canonical.
 * - Product detail pages /dp/<ASIN> -> index with an uppercase-ASIN self-canonical. This preserves the
 *   pre-P0.3 behavior; whether product pages should be indexed at all is an open owner decision.
 * - Everything else (home, categories, deals, trending, tools, tag/filter/list pages) -> index with a
 *   self-canonical ONLY if the exact path is explicitly approved (manifest -> approved paths);
 *   otherwise noindex, follow and NO canonical.
 */
export function evaluateRoute({ pathname, search = '', approved }) {
  const noindex = { index: false, robots: ROBOTS_NOINDEX, canonical: null };
  const path = normalizePath(pathname);
  if (path === null) return { ...noindex, kind: 'invalid' };
  if (typeof search === 'string' && search.replace(/^\?/, '') !== '') return { ...noindex, kind: 'query' };

  const product = PRODUCT_RE.exec(path);
  if (product) {
    return { index: true, robots: ROBOTS_INDEX, canonical: canonicalUrl(`/dp/${product[1].toUpperCase()}`), kind: 'product' };
  }
  if (toPathSet(approved).has(path)) {
    return { index: true, robots: ROBOTS_INDEX, canonical: canonicalUrl(path), kind: path === '/' ? 'home' : 'approved' };
  }
  return { ...noindex, kind: 'unapproved' };
}

/** Title/description for <head>, from the editorial record only; no auto month/year or marketing suffix. */
export function guideMeta(page) {
  const clean = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');
  return {
    title: clean(page?.meta_title) || clean(page?.title),
    description: clean(page?.meta_description) || clean(page?.description),
  };
}

function hasBreadcrumbList(node) {
  if (Array.isArray(node)) return node.some(hasBreadcrumbList);
  if (!node || typeof node !== 'object') return false;
  const type = node['@type'];
  if (type === 'BreadcrumbList' || (Array.isArray(type) && type.includes('BreadcrumbList'))) return true;
  return hasBreadcrumbList(node['@graph']);
}

/**
 * Stored guide HTML may embed its own BreadcrumbList JSON-LD (non-www, invented category URLs such as
 * /accessories/). The page component emits the only valid breadcrumb data, so embedded ones are removed
 * at render time. Other embedded JSON-LD (e.g. FAQPage) and unparseable blocks are left untouched.
 * The database content is not modified.
 */
export function stripEmbeddedBreadcrumbs(html) {
  if (typeof html !== 'string') return html;
  return html.replace(/<script\b[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi, (block, json) => {
    try {
      return hasBreadcrumbList(JSON.parse(json)) ? '' : block;
    } catch {
      return block;
    }
  });
}

/** 'best-computer-monitors' -> 'Best Computer Monitors' (display name for a category slug). */
export function humanizeSlug(slug) {
  return String(slug || '')
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Indexing decision for a REGISTERED static editorial guide (src/content/editorial). Its inputs are only the reviewed
 * fixture and the explicit manifest approval; it never looks at the legacy `seo_auto_pages` record, so a missing,
 * unpublished or unreachable database row cannot change the result (legacy database guides still use evaluateGuide).
 * Indexable only when the guide is "published", has at least 3 listed models, and its exact path is approved.
 */
export function evaluateEditorialGuide({ guide, approved }) {
  const reasons = [];
  if (!guide) reasons.push('guide-missing');
  const path = normalizePath(guide ? `/${guide.slug}` : '');
  if (path === null) reasons.push('invalid-path');
  if (guide && guide.status !== 'published') reasons.push('not-published');
  if (guide && (!Array.isArray(guide.models) || guide.models.length < 3)) reasons.push('too-few-models');
  if (path !== null && !toPathSet(approved).has(path)) reasons.push('not-approved');
  const index = reasons.length === 0;
  return { index, robots: index ? ROBOTS_INDEX : ROBOTS_NOINDEX, canonical: index ? canonicalUrl(path) : null, reasons };
}
